'use strict';

var KayleeBase = require('./KayleeBase');
var KayleeUtil = require('./KayleeUtil');
var log = require('../log/Log').channel('KayleeOffice365');
var sutl = require('../sutl/Sutl');

var KayleeOffice365 = {
    calendarIsEnabled: calendarIsEnabled,
    deleteCalendarAndResults: deleteCalendarAndResults,
    deleteCompanyResults: deleteCompanyResults,
    getAuthorizationStatus: getAuthorizationStatus,
    getCalendarStatus: getCalendarStatus,
    getEnabledCalendarId: getEnabledCalendarId,
    getEnabledCalendarName: getEnabledCalendarName,
    loadAccessToken: loadAccessToken,
    loadAppInfo: loadAppInfo,
    loadAuthenticationResult: loadAuthenticationResult,
    loadAuthorizationResult: loadAuthorizationResult,
    loadAuthorizationSecret: loadAuthorizationSecret,
    loadCalendarId: loadCalendarId,
    loadCalendarInfo: loadCalendarInfo,
    loadDomain: loadDomain,
    setAccessToken: setAccessToken,
    setAuthenticationResult: setAuthenticationResult,
    setAuthorizationResult: setAuthorizationResult,
    setAuthorizationSecret: setAuthorizationSecret,
    setAuthorizationUser: setAuthorizationUser,
    setCalendarNameAndId: setCalendarNameAndId,
    setEvents: setEvents,
    setEventsQueryResult: setEventsQueryResult,
    setEventsSubscriptionResult: setEventsSubscriptionResult,
    setEventsSubscriptionInfo: setEventsSubscriptionInfo,

    paths: {
        authorizationResponse: 'office365AuthorizationResponse',
        eventsWebhook: 'office365EventsWebhook',
    },
};

function calendarIsEnabled(room) {
    var queryResult = sutl.getNode(room, [ 'results', 'office365EventsQuery', 'type' ]);
    var subscriptionResult = sutl.getNode(room, [ 'results', 'office365EventsSubscription', 'type' ]);
    return queryResult === 'ok' && subscriptionResult === 'ok' ? true : false;
}

function deleteCalendarAndResults(obj) {
    log.debug('deleting calendar and results');

    return requireProps(obj, [ 'roomId' ])
    .then(function(obj) {
        var updates = {};
        updates['calendars/' + obj.roomId] = null;
        updates['rooms/' + obj.roomId + '/results/office365CalendarId'] = null;
        updates['rooms/' + obj.roomId + '/results/office365CalendarName'] = null;
        updates['rooms/' + obj.roomId + '/results/office365EventsQuery'] = null;
        updates['rooms/' + obj.roomId + '/results/office365EventsSubscription'] = null;
        return KayleeBase.app.database().ref('/').update(updates);
    })
    .then(function() { return Promise.resolve(obj); });
}

function deleteCompanyResults(obj) {
    log.debug('deleting company results');

    return requireProps(obj, [ 'companyId'] )
    .then(function(obj) {
        var path = 'companies/' + obj.companyId + '/results';
        var updates = {
            office365Authentication: null,
            office365Authorization: null,
            office365Domain: null,
            office365User: null,
        };
        return KayleeBase.app.database().ref(path).update(updates);
    })
    .then(function() { return Promise.resolve(obj); });
}

function getAuthorizationStatus(company) {
    var authorizationResult = sutl.getNode(company,
        [ 'results', 'office365Authorization', 'type' ], null);
    var authenticationResult = sutl.getNode(company,
        [ 'results', 'office365Authentication', 'type' ], null);

    if (authorizationResult === 'ok') {
        if (authenticationResult !== null) {
            return authenticationResult;
        } else {
            return 'waitingForAuth';
        }
    }

    return authorizationResult;
}

function getCalendarStatus(room) {
    // The room results are deleted when office 365 is disabled for the company
    // or if there is a problem with the company-level office 365 auth, so we
    // only need to check room results.

    if (room.hasOwnProperty('results')) {
        if (room.results.hasOwnProperty('office365EventsQuery')) {
            if (room.results.office365EventsQuery.type === 'ok') {
                if (room.results.hasOwnProperty('office365EventsSubscription')) {
                    return room.results.office365EventsSubscription.type;
                }
            } else {
                return room.results.office365EventsQuery.type;
            }
        }
    }

    return null;
}

function getEnabledCalendarId(room) {
    return sutl.getNode(room, [ 'results', 'office365CalendarId' ], null);
}

function getEnabledCalendarName(room) {
    return sutl.getNode(room, [ 'results', 'office365CalendarName' ], null);
}

function loadAccessToken(obj) {
    log.debug('loading access token');
    return requireProps(obj, [ 'companyId' ], [ 'office365AccessToken'])
    .then(function(obj) {
        var path = 'office365/accessTokens/' + obj.companyId;
        var ref = KayleeBase.app.database().ref(path);
        return KayleeUtil.loadData('office365AccessToken', obj, ref, false);
    });
}

function loadAppInfo(obj) {
    log.debug('loading app info');
    return requireProps(obj, [], [ 'office365AppInfo'])
    .then(function(obj) {
        return KayleeUtil.loadData('office365AppInfo', obj,
            KayleeBase.app.database().ref('office365/appInfo'));
    });
}

function loadAuthenticationResult(obj) {
    log.debug('loading authentication result');
    return requireProps(obj, [ 'company' ], [ 'office365AuthenticationResult' ])
    .then(function(obj) {
        obj.office365AuthenticationResult = sutl.getNode(obj, [ 'company', 'results', 'office365Authentication' ]);
        return Promise.resolve(obj);
    });
}

function loadAuthorizationResult(obj) {
    log.debug('loading authorization result');
    return requireProps(obj, [ 'company' ], [ 'office365AuthorizationResult' ])
    .then(function(obj) {
        obj.office365AuthorizationResult = sutl.getNode(obj, [ 'company', 'results', 'office365Authorization' ]);
        return Promise.resolve(obj);
    });
}

function loadAuthorizationSecret(obj) {
    log.debug('loading authorization secret');
    return requireProps(obj, [ 'companyId' ], [ 'office365AuthorizationSecret' ])
    .then(function(obj) {
        var path = 'office365/authorizationSecrets/' + obj.companyId;
        var ref = KayleeBase.app.database().ref(path);
        return KayleeUtil.loadData('office365AuthorizationSecret', obj, ref, false);
    });
}

function loadCalendarId(obj) {
    return requireProps(obj, [ 'room' ], [ 'office365CalendarId' ])
    .then(function(obj) {
        obj.office365CalendarId = sutl.getNode(obj, [ 'room', 'results', 'office365CalendarId' ]);
        return Promise.resolve(obj);
    });
}

function loadCalendarInfo(obj) {
    log.debug('loading office 365 info for calendar');
    return requireProps(obj, [ 'roomId' ], [ 'office365CalendarInfo' ])
    .then(function(obj) {
        var path = 'calendars/' + obj.roomId + '/office365Info';
        var ref = KayleeBase.app.database().ref(path);
        return KayleeUtil.loadData('office365CalendarInfo', obj, ref, false);
    });
}

function loadDomain(obj) {
    log.debug('loading domain');
    return requireProps(obj, [['company', 'results', 'office365Domain' ]], 'office365Domain')
    .then(function(obj) {
        obj.office365Domain = obj.company.results.office365Domain;
        return Promise.resolve(obj);
    });
}

function requireProps(obj, requiredProps, rejectedProps) {
    var i, prop;

    if (typeof rejectedProps === 'undefined') rejectedProps = [];

    for (i = 0; i < rejectedProps.length; i++) {
        prop = rejectedProps[i];

        if (Array.isArray(prop)) {
            if (sutl.nodeExists(obj, prop)) {
                return Promise.reject(new Error(
                    'object already contains ' + prop.join('.')));
            }
        } else {
            if (obj.hasOwnProperty(prop)) {
                return Promise.reject(new Error(
                    'object already contains ' + prop + ' property'));
            }
        }
    }

    for (i = 0; i < requiredProps.length; i++) {
        prop = requiredProps[i];

        if (Array.isArray(prop)) {
            if (!sutl.nodeExists(obj, prop)) {
                return Promise.reject(new Error(
                    'could not find ' + prop.join('.') + ' in object'));
            }
        } else {
            if (!obj.hasOwnProperty(prop)) {
                return Promise.reject(new Error(
                    'could not find ' + prop + ' property in object'));
            }
        }
    }

    return Promise.resolve(obj);
}

function setAccessToken(obj) {
    log.debug('setting access token');
    return requireProps(obj, [ 'companyId', 'office365AccessToken' ])
    .then(function(obj) {
        var path = 'office365/accessTokens/' + obj.companyId;
        return KayleeBase.app.database().ref(path).set(obj.office365AccessToken);
    })
    .then(function() { return Promise.resolve(obj); });
}

function setAuthorizationResult(obj) {
    log.debug('setting authorization result');
    return requireProps(obj, [ 'companyId', 'office365Domain', 'office365AuthorizationResult' ])
    .then(function(obj) {
        if (!obj.office365AuthorizationResult.hasOwnProperty('time')) {
            obj.office365AuthorizationResult.time = Date.now();
        }

        var path = 'companies/' + obj.companyId + '/results';
        var updates = {
            office365Authorization: obj.office365AuthorizationResult,
            office365Domain: obj.office365Domain,
        };
        return KayleeBase.app.database().ref(path).update(updates);
    })
    .then(function() { return Promise.resolve(obj); });
}

function setAuthenticationResult(obj) {
    log.debug('setting authentication result');
    return requireProps(obj, [ 'companyId', 'office365AuthenticationResult' ])
    .then(function(obj) {
        if (!obj.office365AuthenticationResult.hasOwnProperty('time')) {
            obj.office365AuthenticationResult.time = Date.now();
        }

        var path = 'companies/' + obj.companyId + '/results/office365Authentication';
        return KayleeBase.app.database().ref(path).set(obj.office365AuthenticationResult);
    })
    .then(function() { return Promise.resolve(obj); });
}

function setAuthorizationSecret(obj) {
    log.debug('setting authorization secret');
    return requireProps(obj, [ 'companyId', 'office365AuthorizationSecret' ])
    .then(function(obj) {
        var path = 'office365/authorizationSecrets/' + obj.companyId;
        return KayleeBase.app.database().ref(path).set(obj.office365AuthorizationSecret);
    })
    .then(function() { return Promise.resolve(obj); });
}

function setAuthorizationUser(obj) {
    log.debug('setting authorization user');
    return requireProps(obj, [ 'companyId', 'office365AuthorizationUser' ])
    .then(function(obj) {
        var path = 'companies/' + obj.companyId + '/results/office365User';
        return KayleeBase.app.database().ref(path).set(obj.office365AuthorizationUser);
    })
    .then(function() { return Promise.resolve(obj); });
}

function setCalendarNameAndId(obj) {
    log.debug('setting calendar name and id');
    return requireProps(obj, [ 'roomId', 'office365CalendarName', 'office365CalendarId' ])
    .then(function(obj) {
        var path = 'rooms/' + obj.roomId + '/results';
        var updates = {
            office365CalendarId: obj.office365CalendarId,
            office365CalendarName: obj.office365CalendarName,
        };
        return KayleeBase.app.database().ref(path).update(updates);
    })
    .then(function() { return Promise.resolve(obj); });
}

function setEvents(obj) {
    return requireProps(obj, [ 'roomId', 'office365Events', 'office365EventsStart', 'office365EventsEnd' ])
    .then(function(obj) {
        return KayleeBase.app.database().ref('calendars/' + obj.roomId).update({
            'office365Info/eventsStart': obj.office365EventsStart,
            'office365Info/eventsEnd': obj.office365EventsEnd,
            'events': obj.office365Events,
        })
        .then(function() { return Promise.resolve(obj); });
    });
}

function setEventsSubscriptionResult(obj) {
    log.debug('setting events subscription result');
    return requireProps(obj, [ 'roomId', 'office365EventsSubscriptionResult' ])
    .then(function(obj) {
        var path = 'rooms/' + obj.roomId + '/results/office365EventsSubscription';
        if (!obj.office365EventsSubscriptionResult.hasOwnProperty('time')) {
            obj.office365EventsSubscriptionResult.time = Date.now();
        }
        return KayleeBase.app.database().ref(path).set(obj.office365EventsSubscriptionResult);
    })
    .then(function() { return Promise.resolve(obj); });
}

function setEventsSubscriptionInfo(obj) {
    return requireProps(obj, [ 'roomId', 'office365EventsSubscriptionId',
        'office365EventsSubscriptionExpires' ])
    .then(function(obj) {
        var path = 'calendars/' + obj.roomId + '/office365Info';
        return KayleeBase.app.database().ref(path).update({
            subscriptionId: obj.office365EventsSubscriptionId,
            subscriptionExpires: obj.office365EventsSubscriptionExpires,
        });
    })
    .then(function() { return Promise.resolve(obj); });
}

function setEventsQueryResult(obj) {
    log.debug('setting events query result');
    return requireProps(obj, [ 'roomId', 'office365EventsQueryResult' ])
    .then(function(obj) {
        var path = 'rooms/' + obj.roomId + '/results/office365EventsQuery';
        if (!obj.office365EventsQueryResult.hasOwnProperty('time')) {
            obj.office365EventsQueryResult.time = Date.now();
        }
        return KayleeBase.app.database().ref(path).set(obj.office365EventsQueryResult);
    })
    .then(function() { return Promise.resolve(obj); });
}

module.exports = KayleeOffice365;
